import { getPlainDateFromDateUtc } from "@redotech/util/date";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { RedoListingStatus } from "../../redo-listing-status";
import {
  type BuildContext,
  type CycleCount,
  daysSinceLive,
  groupProductsByLocation,
} from "../cycle-count-core";

const MS_PER_DAY = 86_400_000;

export const customRuleGroupModeSchema = createEnum(["all", "any"]);
export type CustomRuleGroupMode = z.infer<typeof customRuleGroupModeSchema>;

export const customRuleAttributeSchema = createEnum([
  "retail_price",
  "unit_cost",
  "inventory_value",
  "on_hand_quantity",
  "days_since_last_count",
  "location",
  "vendor",
  "tags",
  "sku",
  "product_title",
  "product_source",
  "product_status",
  "adjustment_count",
]);
export type CustomRuleAttribute = z.infer<typeof customRuleAttributeSchema>;

export const customRuleOperatorSchema = createEnum([
  "equals",
  "does_not_equal",
  "greater_than",
  "greater_than_or_equal_to",
  "less_than",
  "less_than_or_equal_to",
  "between",
  "contains",
  "does_not_contain",
  "is_contained_in",
  "is_blank",
  "is_not_blank",
  "contains_any",
  "contains_all",
  "contains_none",
  "more_than_days_ago",
  "less_than_days_ago",
]);
export type CustomRuleOperator = z.infer<typeof customRuleOperatorSchema>;

const customRuleConditionValueSchema = z.union([
  z.string(),
  z.number(),
  z.array(z.string()),
  z.tuple([z.number(), z.number()]),
]);
type CustomRuleConditionValue = z.infer<typeof customRuleConditionValueSchema>;

export const customRuleConditionSchema = z.object({
  attribute: customRuleAttributeSchema,
  operator: customRuleOperatorSchema,
  value: customRuleConditionValueSchema.nullable(),
  timeWindowDays: z.int().min(1).max(365).nullable().optional(),
});
export type CustomRuleCondition = z.infer<typeof customRuleConditionSchema>;

export const customRuleConditionGroupSchema = z.object({
  mode: customRuleGroupModeSchema,
  conditions: z.array(customRuleConditionSchema).min(1),
});
export type CustomRuleConditionGroup = z.infer<
  typeof customRuleConditionGroupSchema
>;

const calendarRecurrenceSchema = z.enum(["monthly", "quarterly"]);
export type CalendarRecurrence = z.infer<typeof calendarRecurrenceSchema>;

export const customRuleDataSchema = z.object({
  runEveryDays: z.int().min(1).max(365),
  calendarRecurrence: calendarRecurrenceSchema.nullish(),
  includeGroups: z.array(customRuleConditionGroupSchema).min(1),
  excludeGroups: z.array(customRuleConditionGroupSchema),
});
export type CustomRuleData = z.infer<typeof customRuleDataSchema>;

export interface CustomRule {
  type: "custom";
  data: CustomRuleData;
  liveAt: Date;
}

export interface CustomCycleCountProduct {
  productId: bigint;
  locationId: bigint;
  locationName: string;
  sku: string | null;
  productTitle: string;
  vendor: string | null;
  tags: string[];
  productSource: string | null;
  productStatus: RedoListingStatus;
  retailPrice: number | null;
  unitCost: number | null;
  onHandQuantity: number;
  inventoryValue: number | null;
  lastCountedAt: Date | null;
  adjustmentCount: number;
  adjustmentEvents: Date[];
  hasOpenCount: boolean;
}

export interface CustomCycleCountProductMatch extends CustomCycleCountProduct {
  included: boolean;
  excluded: boolean;
}

interface EvaluateCustomInput {
  rule: CustomRule;
  products: CustomCycleCountProduct[];
  now: Date;
}

export function evaluateCustomCycleCountProducts({
  rule,
  products,
  now,
}: EvaluateCustomInput): CustomCycleCountProductMatch[] {
  return products.map((product) => {
    const included = matchesAnyGroup(rule.data.includeGroups, product, now);
    const excluded =
      included && matchesAnyGroup(rule.data.excludeGroups, product, now);
    return { ...product, included, excluded };
  });
}

export function buildCustomCycleCounts(
  { rule, now, ignoreSchedule }: BuildContext<CustomRule>,
  products: CustomCycleCountProduct[],
): CycleCount[] {
  if (!ignoreSchedule && !isRuleDue(rule, now)) {
    return [];
  }

  const matched = evaluateCustomCycleCountProducts({ rule, products, now });
  const included = matched.filter(
    (product) => !product.hasOpenCount && product.included && !product.excluded,
  );
  return groupProductsByLocation(included);
}

function isRuleDue(rule: CustomRule, now: Date): boolean {
  const recurrence = rule.data.calendarRecurrence;
  if (recurrence == null) {
    const days = daysSinceLive(rule.liveAt, now);
    return days >= 0 && days % rule.data.runEveryDays === 0;
  }

  const liveDate = getPlainDateFromDateUtc(rule.liveAt);
  const today = getPlainDateFromDateUtc(now);
  if (Temporal.PlainDate.compare(today, liveDate) < 0) {
    return false;
  }
  if (Temporal.PlainDate.compare(today, liveDate) === 0) {
    return true;
  }
  const monthsSinceLive =
    (today.year - liveDate.year) * 12 + today.month - liveDate.month;
  const recurrenceMonths = recurrence === "monthly" ? 1 : 3;
  if (monthsSinceLive <= 0 || monthsSinceLive % recurrenceMonths !== 0) {
    return false;
  }

  const occurrence = today
    .toPlainYearMonth()
    .toPlainDate({ day: Math.min(liveDate.day, today.daysInMonth) });
  return Temporal.PlainDate.compare(today, occurrence) === 0;
}

function matchesAnyGroup(
  groups: CustomRuleConditionGroup[],
  product: CustomCycleCountProduct,
  now: Date,
): boolean {
  return groups.some((group) => matchesGroup(group, product, now));
}

function matchesGroup(
  group: CustomRuleConditionGroup,
  product: CustomCycleCountProduct,
  now: Date,
): boolean {
  const matches = group.conditions.map((condition) =>
    matchesCondition(condition, product, now),
  );
  return group.mode === "all" ? matches.every(Boolean) : matches.some(Boolean);
}

function matchesCondition(
  condition: CustomRuleCondition,
  product: CustomCycleCountProduct,
  now: Date,
): boolean {
  const value = valueForAttribute(condition, product, now);
  switch (condition.operator) {
    case "equals":
      return equalsValue(value, condition.value);
    case "does_not_equal":
      return !equalsValue(value, condition.value);
    case "is_contained_in":
      return scalarContainedIn(value, condition.value);
    case "greater_than":
    case "greater_than_or_equal_to":
    case "less_than":
    case "less_than_or_equal_to":
    case "between":
      return compareNumeric(value, condition.operator, condition.value);
    case "contains":
    case "does_not_contain": {
      const contains =
        typeof value === "string" &&
        typeof condition.value === "string" &&
        value.toLowerCase().includes(condition.value.toLowerCase());
      return condition.operator === "contains" ? contains : !contains;
    }
    case "is_blank":
      return (
        value == null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0)
      );
    case "is_not_blank":
      return !(
        value == null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0)
      );
    case "contains_any":
    case "contains_all":
    case "contains_none":
      return compareList(value, condition.operator, condition.value);
    case "more_than_days_ago":
    case "less_than_days_ago":
      return compareNumeric(value, condition.operator, condition.value);
    default:
      return assertNever(condition.operator);
  }
}

function valueForAttribute(
  condition: CustomRuleCondition,
  product: CustomCycleCountProduct,
  now: Date,
): number | string | string[] | null {
  switch (condition.attribute) {
    case "retail_price":
      return product.retailPrice;
    case "unit_cost":
      return product.unitCost;
    case "inventory_value":
      return product.inventoryValue;
    case "on_hand_quantity":
      return product.onHandQuantity;
    case "days_since_last_count":
      return product.lastCountedAt == null
        ? Number.POSITIVE_INFINITY
        : Math.floor(
            (now.getTime() - product.lastCountedAt.getTime()) / MS_PER_DAY,
          );
    case "location":
      return product.locationName;
    case "vendor":
      return product.vendor;
    case "tags":
      return product.tags;
    case "sku":
      return product.sku;
    case "product_title":
      return product.productTitle;
    case "product_source":
      return product.productSource;
    case "product_status":
      return product.productStatus;
    case "adjustment_count":
      if (condition.timeWindowDays) {
        const windowStart =
          now.getTime() - condition.timeWindowDays * MS_PER_DAY;
        return product.adjustmentEvents.filter(
          (event) => event.getTime() >= windowStart,
        ).length;
      }
      return product.adjustmentCount;
  }
}

function equalsValue(
  value: number | string | string[] | null,
  expected: CustomRuleConditionValue | null,
): boolean {
  if (Array.isArray(value)) {
    return typeof expected === "string" && value.includes(expected);
  }
  if (typeof value === "string" && Array.isArray(expected)) {
    return expected
      .filter((item): item is string => typeof item === "string")
      .some((item) => item.toLowerCase() === value.toLowerCase());
  }
  if (typeof value === "string" && typeof expected === "string") {
    return value.toLowerCase() === expected.toLowerCase();
  }
  return value === expected;
}

function scalarContainedIn(
  value: number | string | string[] | null,
  expected: CustomRuleConditionValue | null,
): boolean {
  if (typeof value !== "string" || !Array.isArray(expected)) {
    return false;
  }
  const normalizedValue = value.toLowerCase();
  return expected
    .filter((item): item is string => typeof item === "string")
    .some((item) => item.toLowerCase() === normalizedValue);
}

function compareNumeric(
  value: number | string | string[] | null,
  operator: CustomRuleOperator,
  expected: CustomRuleConditionValue | null,
): boolean {
  if (typeof value !== "number") {
    return false;
  }
  if (operator === "between") {
    return Array.isArray(expected) &&
      expected.length === 2 &&
      typeof expected[0] === "number" &&
      typeof expected[1] === "number"
      ? value >= expected[0] && value <= expected[1]
      : false;
  }
  if (typeof expected !== "number") {
    return false;
  }
  switch (operator) {
    case "greater_than":
    case "more_than_days_ago":
      return value > expected;
    case "greater_than_or_equal_to":
      return value >= expected;
    case "less_than":
    case "less_than_days_ago":
      return value < expected;
    case "less_than_or_equal_to":
      return value <= expected;
    default:
      return value === expected;
  }
}

function compareList(
  value: number | string | string[] | null,
  operator: CustomRuleOperator,
  expected: CustomRuleConditionValue | null,
): boolean {
  if (!Array.isArray(value) || !Array.isArray(expected)) {
    return false;
  }
  const normalized = new Set(value.map((item) => item.toLowerCase()));
  const expectedValues = expected
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.toLowerCase());
  switch (operator) {
    case "contains_any":
      return expectedValues.some((item) => normalized.has(item));
    case "contains_all":
      return expectedValues.every((item) => normalized.has(item));
    case "contains_none":
      return expectedValues.every((item) => !normalized.has(item));
    default:
      return false;
  }
}
